import { Col, Form, Input, Modal, Row, Spin } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MerchantRequest } from "@/models/merchant";
import showNotification from "@/components/common/notification";
import "./index.scss";
import { MerchantApi } from "@/apis/merchant.api";
import LocationContext from "../hook/locationContext";
import { LocationResponseModel } from "@/models/location";

export interface MerchantModalProps {
  visible: boolean;
  onClose:(isUpdated?: boolean) => void; 
  reloadLocationData?: () => void;
  currentId?: string;
  parentItem?: LocationResponseModel | null;
  locationId?: string;
}

export function ModalUpdateMerchant(props: MerchantModalProps) {
  const { t } = useTranslation();
  const [merchantForm] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);

  useEffect(() => {
    if(props.visible){
      if (props.currentId) {
        fetchMerchantData(props.currentId);
      }
  
      merchantForm.resetFields();
      merchantForm.setFieldValue("locationParentId", props.parentItem?.id);
  
    }
  }, [props.visible]); // Empty dependency array ensures this runs once when the component mounts.

const fetchMerchantData = async (merchantId : string) => {
  setLoading(true);
  try {
    const response = await MerchantApi.getMerchant(merchantId, props.locationId);
    const data = response.data;

    merchantForm.setFieldsValue({
      merchantName: data.lszMerchantName,
      emailAddress: data.lszEmailAddress,
      phone: data.lszPhone,
      mobilePhone: data.lszMobilePhone,
      streetAddressGeo: data.lszStreetAddressGeo,
      locationId: data.locationId
    });
  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    setLoading(false);
  }
};

  const handleOk = () => {
    merchantForm.submit();
  };

  const handleCancel = () => {
    merchantForm.resetFields();
    props.onClose();
  };

  const handleCancelUpdated = () => {
    merchantForm.resetFields();
    props.onClose(true);
  };

  async function handleMerchantOperation(values: any, isUpdate: boolean) {
    setIsEditLoading(true);
    try {
      const model: MerchantRequest = {
        ...(isUpdate && { id: props.currentId }),
        locationId: values.locationParentId,
        lszMerchantName: values.merchantName,
        lszEmailAddress: values.emailAddress,
        lszPhone: values.phone,
        lszMobilePhone: values.mobilePhone,
        lszStreetAddressGeo: values.streetAddressGeo,
        lszIPGeo: values.ipGeo,
      };

      const data = isUpdate
        ? await MerchantApi.updateMerchant(props.currentId!, model)
        : await MerchantApi.createMerchant(model);

      if (data) {
        showNotification("success", t(isUpdate ? "Update Location successfully." : "Create Location successfully."));
        props.reloadLocationData && props.reloadLocationData();
        handleCancelUpdated();
      }
    } catch (error) {
    } finally {
      setIsEditLoading(false);
    }
  }


  async function handleFormSubmit(values: any) {
    if (props.currentId) {
      await handleMerchantOperation(values, true);
    } else {
      await handleMerchantOperation(values, false);
    }
  }

  function MerchantForm() {
    return (
      <>
        <Form
          className="modal-container"
          layout="vertical"
          labelWrap
          form={merchantForm}
          onFinish={handleFormSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Merchant Name"
                name="merchantName"
                rules={[{ required: true, message: 'Please input the merchant name!' }]}
              >
                <Input className="input-base" placeholder="Enter Merchant Name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Email Address"
                name="emailAddress"
              >
                <Input className="input-base" placeholder="Enter Email Address" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Phone"
                name="phone"
              >
                <Input className="input-base" placeholder="Enter Phone Number" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Mobile Phone"
                name="mobilePhone"
              >
                <Input className="input-base" placeholder="Enter Mobile Phone Number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Street Address"
                name="streetAddressGeo"
              >
                <Input className="input-base" placeholder="Enter Street Address" />
              </Form.Item>
            </Col>
            {!props.currentId && props.parentItem && (
              <Col span={12}>
                <Form.Item
                  label="Parent Location"
                  name="locationParentName"
                >
                  <Input defaultValue={props.parentItem.name} className="input-base" style={{ width: '100%' }} disabled />
                </Form.Item>

                <Form.Item
                  name="locationParentId"
                  hidden
                />
              </Col>
            )}  
          </Row>
        </Form>
      </>
    );
  }

  return (
    <>
      <Modal
        className="modal-setup"
        style={{ top: 170 }}
        width={800}
        open={props.visible}
        title={props.currentId ? "Edit Merchant" : "New Merchant"}
        onCancel={handleCancel}
        okButtonProps={{ loading: isEditLoading }}
        onOk={handleOk}
        maskClosable={false}
      >
        <Spin spinning={isLoading}>
          <MerchantForm />
        </Spin>
      </Modal>
    </>
  );
}

export default ModalUpdateMerchant;
